import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { formatCurrency, formatDate, MONTHS, CURRENCIES, downloadCSV } from '../lib/utils';
import { Plus, Upload, Download, Pencil, Trash2, Loader2, CalendarIcon, Search, Filter, X, FileDown, MessageSquare, XCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function InvoicesPage() {
  const { canEdit } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState('');
  const [formData, setFormData] = useState({
    client_id: '',
    invoice_no: '',
    billing_month: '',
    billing_year: new Date().getFullYear(),
    payment_date: null,
    paid_amount_inr: '',
    bill_amount: '',
    bill_currency: 'USD',
    notes: ''
  });
  const [filters, setFilters] = useState({
    financial_year: '',
    invoice_status: '',
    client_name: '',
    bill_currency: ''
  });
  const [financialYears, setFinancialYears] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (filters.financial_year) params.financial_year = filters.financial_year;
      if (filters.invoice_status) params.invoice_status = filters.invoice_status;
      if (filters.client_name) params.client_name = filters.client_name;
      if (filters.bill_currency) params.bill_currency = filters.bill_currency;

      const response = await axios.get(`${API_URL}/invoices`, { params });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchFinancialYears = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setFinancialYears(response.data.financial_years || []);
    } catch (error) {
      console.error('Failed to fetch FY:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchFinancialYears();
  }, [fetchInvoices]);

  // Auto-populate bill amount and currency when client is selected
  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => c.id === clientId);
    if (selectedClient) {
      setFormData({
        ...formData,
        client_id: clientId,
        bill_amount: selectedClient.contract_amount?.toString() || '',
        bill_currency: selectedClient.contract_currency || 'USD'
      });
    } else {
      setFormData({ ...formData, client_id: clientId });
    }
  };

  const openCreateDialog = () => {
    setSelectedInvoice(null);
    setFormData({
      client_id: '',
      invoice_no: '',
      billing_month: '',
      billing_year: new Date().getFullYear(),
      payment_date: null,
      paid_amount_inr: '',
      bill_amount: '',
      bill_currency: 'USD',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      client_id: invoice.client_id,
      invoice_no: invoice.invoice_no?.toString() || '',
      billing_month: invoice.billing_month,
      billing_year: invoice.billing_year || new Date().getFullYear(),
      payment_date: invoice.payment_date ? new Date(invoice.payment_date) : null,
      paid_amount_inr: invoice.paid_amount_inr?.toString() || '',
      bill_amount: invoice.bill_amount?.toString() || '',
      bill_currency: invoice.bill_currency,
      notes: invoice.notes || ''
    });
    setDialogOpen(true);
  };

  const openNotesDialog = (notes) => {
    setSelectedNotes(notes || 'No notes available');
    setNotesDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.client_id || !formData.billing_month || !formData.bill_amount || !formData.billing_year) {
      toast.error('Please fill required fields (including billing year)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        billing_year: parseInt(formData.billing_year),
        invoice_no: formData.invoice_no?.trim() || null,
        paid_amount_inr: formData.paid_amount_inr ? parseFloat(formData.paid_amount_inr) : 0,
        bill_amount: parseFloat(formData.bill_amount),
        payment_date: formData.payment_date ? format(formData.payment_date, 'yyyy-MM-dd') : null
      };

      // Remove invoice_no if null or empty (for create, let backend auto-generate)
      if (!payload.invoice_no) {
        delete payload.invoice_no;
      }

      if (selectedInvoice) {
        await axios.put(`${API_URL}/invoices/${selectedInvoice.id}`, payload);
        toast.success('Invoice updated successfully');
      } else {
        await axios.post(`${API_URL}/invoices`, payload);
        toast.success('Invoice created successfully');
      }
      setDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleClearPaymentDate = async () => {
    if (!selectedInvoice) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/invoices/${selectedInvoice.id}`, {
        clear_payment_date: true
      });
      toast.success('Payment date cleared');
      setFormData({ ...formData, payment_date: null });
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to clear payment date');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;

    try {
      await axios.delete(`${API_URL}/invoices/${selectedInvoice.id}`);
      toast.success('Invoice deleted successfully');
      setDeleteDialogOpen(false);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const [importMode, setImportMode] = useState('create');

  const handleRemoveDuplicates = async () => {
    if (!window.confirm('This will permanently remove all duplicate invoices (keeping the original). Continue?')) {
      return;
    }
    
    setRemovingDuplicates(true);
    try {
      const response = await axios.post(`${API_URL}/invoices/remove-duplicates`);
      const { removed, duplicate_groups, message } = response.data;
      
      if (removed > 0) {
        toast.success(message);
        fetchInvoices();
      } else {
        toast.info('No duplicate invoices found');
      }
    } catch (error) {
      toast.error('Failed to remove duplicates');
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/import/invoices?mode=${importMode}`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { created, updated, skipped, errors } = response.data;
      if (errors?.length > 0) {
        toast.warning(`Import: ${created} created, ${updated} updated, ${skipped} skipped, ${errors.length} errors`);
      } else {
        toast.success(`Import complete: ${created} created, ${updated} updated, ${skipped} skipped`);
      }
      setImportDialogOpen(false);
      setImportMode('create');
      fetchInvoices();
    } catch (error) {
      toast.error('Import failed');
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (filters.financial_year) params.financial_year = filters.financial_year;
      if (filters.invoice_status) params.invoice_status = filters.invoice_status;
      if (filters.bill_currency) params.bill_currency = filters.bill_currency;

      const response = await axios.get(`${API_URL}/export/invoices`, { params });
      downloadCSV(response.data, 'invoices_export');
      toast.success('Export successful');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const downloadTemplate = () => {
    const templateData = [{
      client_name: 'Example Client',
      billing_month: 'January',
      billing_year: '2024',
      payment_date: '2024-04-15',
      paid_amount_inr: '85000',
      bill_amount: '1000',
      bill_currency: 'USD',
      notes: 'Sample invoice note'
    }];
    downloadCSV(templateData, 'invoices_import_template');
    toast.success('Template downloaded');
  };

  // Calculate grand totals for the displayed invoices
  const calculateGrandTotals = () => {
    return invoices.reduce((totals, invoice) => {
      return {
        bill_amount: totals.bill_amount + (invoice.bill_amount || 0),
        paid_amount_inr: totals.paid_amount_inr + (invoice.paid_amount_inr || 0),
        estimated_revenue_inr: totals.estimated_revenue_inr + (invoice.estimated_revenue_inr || 0)
      };
    }, { bill_amount: 0, paid_amount_inr: 0, estimated_revenue_inr: 0 });
  };

  const grandTotals = calculateGrandTotals();

  const clearFilters = () => {
    setFilters({
      financial_year: '',
      invoice_status: '',
      client_name: '',
      bill_currency: ''
    });
  };

  const getStatusBadge = (status) => {
    const classes = {
      'Paid': 'badge-paid',
      'Unpaid': 'badge-unpaid'
    };
    return <span className={`badge ${classes[status] || ''}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Invoices</h1>
            <p className="text-muted-foreground">Manage your invoices and track payments</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {canEdit() && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleRemoveDuplicates} 
                  disabled={removingDuplicates}
                  className="text-destructive border-destructive/50 hover:bg-destructive/10"
                  data-testid="remove-duplicates-btn"
                >
                  {removingDuplicates ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Remove Duplicates
                </Button>
                <Button variant="outline" onClick={downloadTemplate} data-testid="download-invoice-template-btn">
                  <FileDown className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
                <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-invoices-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button onClick={openCreateDialog} data-testid="add-invoice-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Invoice
                </Button>
              </>
            )}
            <Button variant="outline" onClick={handleExport} data-testid="export-invoices-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              <Select value={filters.financial_year || "all"} onValueChange={(v) => setFilters({...filters, financial_year: v === "all" ? "" : v})}>
                <SelectTrigger className="w-40" data-testid="filter-fy">
                  <SelectValue placeholder="Financial Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {financialYears.map(fy => (
                    <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.invoice_status || "all"} onValueChange={(v) => setFilters({...filters, invoice_status: v === "all" ? "" : v})}>
                <SelectTrigger className="w-36" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filters.bill_currency || "all"} onValueChange={(v) => setFilters({...filters, bill_currency: v === "all" ? "" : v})}>
                <SelectTrigger className="w-32" data-testid="filter-currency">
                  <SelectValue placeholder="Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {CURRENCIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search client..."
                  value={filters.client_name}
                  onChange={(e) => setFilters({...filters, client_name: e.target.value})}
                  className="pl-9 w-48"
                  data-testid="filter-search"
                />
              </div>

              {(filters.financial_year || filters.invoice_status || filters.bill_currency || filters.client_name) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5">
                    <TableHead>Invoice No</TableHead>
                    <TableHead>Client Name</TableHead>
                    <TableHead>Billing Month</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead className="text-right">Bill Amount</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">Paid (INR)</TableHead>
                    <TableHead className="text-right">Exchange Rate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Notes</TableHead>
                    {canEdit() && <TableHead className="text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit() ? 11 : 10} className="text-center py-8 text-muted-foreground">
                        No invoices found. {canEdit() && 'Click "Add Invoice" to create one.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.invoice_no}`}>
                        <TableCell className="font-mono font-medium">{invoice.invoice_no}</TableCell>
                        <TableCell>{invoice.client_name}</TableCell>
                        <TableCell>
                          {invoice.billing_month}
                          {invoice.billing_year && <span className="text-muted-foreground text-xs ml-1">({invoice.billing_year})</span>}
                        </TableCell>
                        <TableCell>{formatDate(invoice.payment_date)}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(invoice.bill_amount, invoice.bill_currency)}
                        </TableCell>
                        <TableCell>{invoice.bill_currency}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(invoice.paid_amount_inr)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {invoice.exchange_rate?.toFixed(4) || '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(invoice.invoice_status)}</TableCell>
                        <TableCell className="text-center">
                          {invoice.notes ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openNotesDialog(invoice.notes)}
                                  className="h-8 w-8"
                                >
                                  <MessageSquare className="h-4 w-4 text-primary" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">{invoice.notes.substring(0, 100)}{invoice.notes.length > 100 ? '...' : ''}</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        {canEdit() && (
                          <TableCell>
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => openEditDialog(invoice)}
                                data-testid={`edit-invoice-${invoice.invoice_no}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => { setSelectedInvoice(invoice); setDeleteDialogOpen(true); }}
                                data-testid={`delete-invoice-${invoice.invoice_no}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                  {/* Grand Total Row */}
                  {invoices.length > 0 && (
                    <TableRow className="bg-primary/10 font-bold border-t-2 border-primary">
                      <TableCell className="font-semibold">
                        Grand Total ({invoices.length} invoices)
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(grandTotals.bill_amount)}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(grandTotals.paid_amount_inr)}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      {canEdit() && <TableCell></TableCell>}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
              <DialogDescription>
                {selectedInvoice ? 'Update invoice details' : 'Enter invoice details to create a new invoice'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="invoice-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>{client.client_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Bill amount & currency will auto-populate from client contract</p>
              </div>

              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input
                  type="text"
                  placeholder={selectedInvoice ? selectedInvoice.invoice_no : 'e.g., INV-2024-001 (auto-generated if empty)'}
                  value={formData.invoice_no}
                  onChange={(e) => setFormData({...formData, invoice_no: e.target.value})}
                  data-testid="invoice-no-input"
                />
                <p className="text-xs text-muted-foreground">Leave empty to auto-generate (INV-YYYY-NNNN format)</p>
              </div>

              <div className="space-y-2">
                <Label>Currency *</Label>
                <Select value={formData.bill_currency} onValueChange={(v) => setFormData({...formData, bill_currency: v})}>
                  <SelectTrigger data-testid="invoice-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Billing Month *</Label>
                <Select value={formData.billing_month} onValueChange={(v) => setFormData({...formData, billing_month: v})}>
                  <SelectTrigger data-testid="invoice-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Billing Year *</Label>
                <Select 
                  value={formData.billing_year?.toString()} 
                  onValueChange={(v) => setFormData({...formData, billing_year: parseInt(v)})}
                >
                  <SelectTrigger data-testid="invoice-year-select">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Show years from 2020 to current year + 2 */}
                    {Array.from({ length: new Date().getFullYear() - 2020 + 3 }, (_, i) => 2020 + i).map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Paid Amount (INR)</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.paid_amount_inr}
                  onChange={(e) => setFormData({...formData, paid_amount_inr: e.target.value})}
                  data-testid="invoice-paid-amount"
                />
              </div>

              <div className="space-y-2">
                <Label>Payment Date</Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn("flex-1 justify-start text-left font-normal", !formData.payment_date && "text-muted-foreground")}
                        data-testid="invoice-payment-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.payment_date ? format(formData.payment_date, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.payment_date}
                        onSelect={(date) => setFormData({...formData, payment_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {formData.payment_date && (
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setFormData({...formData, payment_date: null})}
                      title="Clear payment date"
                    >
                      <XCircle className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Exchange Rate</Label>
                <Input
                  value={formData.paid_amount_inr && formData.bill_amount 
                    ? (parseFloat(formData.paid_amount_inr) / parseFloat(formData.bill_amount)).toFixed(4)
                    : 'Auto-calculated'}
                  disabled
                  className="bg-muted"
                  data-testid="invoice-exchange-rate"
                />
              </div>

              {/* Estimated Revenue - shown only in edit mode */}
              {selectedInvoice && (
                <div className="space-y-2">
                  <Label>Estimated Revenue (INR)</Label>
                  <Input
                    value={selectedInvoice.estimated_revenue_inr 
                      ? formatCurrency(selectedInvoice.estimated_revenue_inr)
                      : selectedInvoice.exchange_rate && selectedInvoice.bill_amount
                        ? formatCurrency(selectedInvoice.exchange_rate * selectedInvoice.bill_amount)
                        : 'N/A (Paid invoice)'}
                    disabled
                    className="bg-amber-50 font-mono font-semibold"
                    data-testid="invoice-estimated-revenue"
                  />
                  <p className="text-xs text-muted-foreground">
                    Calculated: Bill Amount × Exchange Rate
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Bill Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.bill_amount}
                  onChange={(e) => setFormData({...formData, bill_amount: e.target.value})}
                  data-testid="invoice-bill-amount"
                />
              </div>

              <div className="col-span-2 space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Add any notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  data-testid="invoice-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-invoice-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {selectedInvoice ? 'Update Invoice' : 'Save Invoice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notes Dialog */}
        <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invoice Notes</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm whitespace-pre-wrap">{selectedNotes}</p>
            </div>
            <DialogFooter>
              <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) setImportMode('create'); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Invoices</DialogTitle>
              <DialogDescription>
                Upload a CSV file with invoice data. Include &apos;id&apos; column to update existing invoices.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Import Mode</Label>
                <Select value={importMode} onValueChange={setImportMode}>
                  <SelectTrigger data-testid="invoice-import-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create">Create Only (skip existing)</SelectItem>
                    <SelectItem value="upsert">Upsert (update existing by ID, create new)</SelectItem>
                    <SelectItem value="update_only">Update Only (skip new records)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {importMode === 'upsert' && 'Include "id" column from export to update existing invoices'}
                  {importMode === 'create' && 'New invoices will be created, existing ones skipped'}
                  {importMode === 'update_only' && 'Only rows with matching "id" will be updated'}
                </p>
              </div>
              <div className="import-dropzone">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="invoice-import"
                />
                <label htmlFor="invoice-import" className="cursor-pointer">
                  <Upload className="h-10 w-10 mx-auto text-primary/50 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Click to select CSV file or drag and drop
                  </p>
                </label>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Invoice</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete invoice #{selectedInvoice?.invoice_no}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-invoice">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
